// Contract shared with the API (party-pictures-api/api/lib/qr-style.js) and the
// app (qr_style.dart). Pure: no DOM, no Nuxt.

export const QR_MODULE_SHAPES = ['square', 'rounded', 'dots', 'fluid']
export const QR_EYE_SHAPES = ['square', 'rounded', 'circle', 'leaf']
export const QR_IMAGE_KINDS = ['none', 'initials', 'photo', 'heart']
export const QR_KNOCKOUTS = ['circle', 'rounded', 'none']
export const QR_IMAGE_SIZE = { MIN: 0.16, MAX: 0.34, DEFAULT: 0.24, STEP: 0.01 }
export const QR_INITIALS_MAX_LENGTH = 4
export const DEFAULT_QR_COLOR = '#1f2760'
export const QR_COLOR_PRESETS = ['#1f2760', '#000000', '#e53935', '#2e7d32', '#1565c0', '#6a1b9a']

export const DEFAULT_QR_STYLE = Object.freeze({
  v: 1,
  moduleShape: 'square',
  eyeShape: 'square',
  color: DEFAULT_QR_COLOR,
  image: Object.freeze({
    kind: 'none',
    knockout: 'circle',
    size: QR_IMAGE_SIZE.DEFAULT,
    initials: '',
    color: null,
    photo: null,
    photoUrl: null,
  }),
})

/** @returns {QrStyle} a fresh, mutable copy of the default */
export function defaultQrStyle() {
  return { ...DEFAULT_QR_STYLE, image: { ...DEFAULT_QR_STYLE.image } }
}

const isObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value)
const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback)

/**
 * #RGB is rejected on purpose: the generators and the API only accept 6 digits.
 * app-input type="color" emits #RRGGBBAA, so a trailing alpha pair is dropped.
 * @param {unknown} value
 * @returns {string} lower-case #rrggbb
 */
export function normalizeQrColor(value) {
  const hex = String(value ?? '').trim()

  return /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex) ? hex.slice(0, 7).toLowerCase() : DEFAULT_QR_COLOR
}

function normalizeSize(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return QR_IMAGE_SIZE.DEFAULT
  }

  const clamped = Math.min(QR_IMAGE_SIZE.MAX, Math.max(QR_IMAGE_SIZE.MIN, value))

  return Math.round(clamped * 100) / 100
}

/**
 * Artwork colour for initials and heart; null means "same as the QR colour".
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeQrImageColor(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const hex = normalizeQrColor(value)

  return hex === DEFAULT_QR_COLOR && !/^#1f2760/i.test(String(value).trim()) ? null : hex
}

function normalizeInitials(value) {
  return typeof value === 'string' ? value.trim().slice(0, QR_INITIALS_MAX_LENGTH) : ''
}

const stringOrNull = (value) => (typeof value === 'string' && value.length ? value : null)

/**
 * Tolerant: anything unusable falls back per field. Accepts a legacy colour
 * string (slideshow, old callers), a partial object (API answer) or nothing.
 * @param {unknown} input
 * @returns {QrStyle}
 */
export function normalizeQrStyle(input) {
  if (typeof input === 'string') {
    return { ...defaultQrStyle(), color: normalizeQrColor(input) }
  }

  if (!isObject(input)) {
    return defaultQrStyle()
  }

  const image = isObject(input.image) ? input.image : {}
  const photo = stringOrNull(image.photo)
  const kind = pick(image.kind, QR_IMAGE_KINDS, 'none')

  return {
    v: 1,
    moduleShape: pick(input.moduleShape, QR_MODULE_SHAPES, 'square'),
    eyeShape: pick(input.eyeShape, QR_EYE_SHAPES, 'square'),
    color: normalizeQrColor(input.color),
    image: {
      kind: kind === 'photo' && !photo ? 'none' : kind,
      knockout: pick(image.knockout, QR_KNOCKOUTS, 'circle'),
      size: normalizeSize(image.size),
      initials: normalizeInitials(image.initials),
      color: normalizeQrImageColor(image.color),
      photo,
      photoUrl: stringOrNull(image.photoUrl),
    },
  }
}

/** @returns {'M'|'H'} error correction: H pays for the modules an image covers */
export function eclFor(style) {
  return style.image.kind === 'none' ? 'M' : 'H'
}

/**
 * The style to draw with, given what could actually be loaded.
 * @param {QrStyle} style
 * @param {{ hasPhoto: boolean }} availability
 * @returns {QrStyle}
 */
export function resolveRenderStyle(style, { hasPhoto }) {
  const kind = style.image.kind === 'photo' && !hasPhoto ? 'none' : style.image.kind

  return { ...style, image: { ...style.image, kind } }
}

/**
 * Body for PUT /event/:id/qr-style. The photo path is server-owned and never sent.
 * @param {QrStyle} style
 */
export function toQrStylePayload(style) {
  return {
    moduleShape: style.moduleShape,
    eyeShape: style.eyeShape,
    color: style.color,
    image: {
      kind: style.image.kind,
      knockout: style.image.knockout,
      size: style.image.size,
      initials: style.image.initials,
      color: style.image.color,
    },
  }
}

/** The colour the centre artwork (initials disc, heart) is drawn in. */
export function qrArtworkColor(style) {
  return style.image.color ?? style.color
}
